import React from "react";
import { Img } from "@/components/Img";
import { Column } from "@/components/Stack";
import { Text } from "@/components/Text";
import { useFocusable } from "@noriginmedia/norigin-spatial-navigation";
import { TickIcon } from "@/components/Icons";
import { Culture, Dialect } from "@/types/setting.types";
import { focusKeyHelper } from "@/utils/focusKeys.helpers";
import { SETTINGS_PAGE_SCROLLABLE_CONTAINER_ID } from "@/constants/constants";
import { shoudlPreventResponsivenessOnLowerLg } from "@/utils/common.helpers";

const transitionClassName = "transition-all duration-200";

export const CultureSelectionItemTv = ({
  culture,
  isSelected,
  onClick,
  isDisabled = false,
}: {
  culture: Culture;
  isSelected: boolean;
  onClick: () => void;
  isDisabled?: boolean;
}) => {
  const _shoudlPreventResponsivenessOnLowerLg =
    shoudlPreventResponsivenessOnLowerLg();
  const { ref, focused } = useFocusable({
    focusKey: focusKeyHelper.getCultureSelectionItemFocusKey(
      culture.abbreviation,
    ),
    onEnterPress: onClick,
    onFocus: (e) => {
      const scrollableContainer = document.getElementById(
        SETTINGS_PAGE_SCROLLABLE_CONTAINER_ID,
      );
      if (scrollableContainer && scrollableContainer.scrollTop > 0) {
        scrollableContainer.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    },
  });

  return (
    <button
      ref={ref}
      onClick={onClick}
      disabled={isDisabled}
      className={`focus:outline-none ${isDisabled ? "cursor-not-allowed opacity-50" : ""}`}
    >
      <Column justify="center" align="center" gap={2} useGrid>
        <div
          className={`relative h-32 w-32 overflow-hidden rounded-xl ${!_shoudlPreventResponsivenessOnLowerLg ? "xl:h-44 xl:w-44" : ""} ${transitionClassName} ${focused ? "scale-110 border-4 border-white" : ""}`}
        >
          <Img
            src={`${process.env.NEXT_PUBLIC_CDN_ROOT_URL}${culture.imageUrl}`}
            alt={culture.name}
            fill={true}
            // Scale is required to eliminate empty space around the image when using border-4 with rounded corners
            className="scale-105 object-cover"
          />
          {isSelected && (
            <div
              className={`absolute right-1 top-1 flex items-center justify-center rounded-full bg-stg-primary-cta p-2`}
            >
              <TickIcon color="white" width={16} height={16} />
            </div>
          )}
        </div>
        <Text
          transition
          weight={focused ? "bold" : "medium"}
          alignment="center"
          appearance={isSelected || focused ? "primary" : "secondary"}
        >
          <span
            className={`text-xl ${!_shoudlPreventResponsivenessOnLowerLg ? "xl:text-[1.375rem]" : ""}`}
          >
            {culture.title}
          </span>
        </Text>
      </Column>
    </button>
  );
};
